import { allWorkspacesSelector, chatsInView } from "../../../utils/workspaceView";
import React, { useCallback, useMemo, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import ChatListRow from "./ChatListRow";
import { Box, Button, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useTranslation } from "react-i18next";
import {
  CustomInput,
  SearchBox,
  ChatSearchButton,
  SmallIcon,
} from "../../../common/chatComponents/ScreenSearchSection";
import PersonIcon from "../../../assets/images/chats/person.svg";
import searchIcon from "../../../assets/images/chats/gallery_active.svg";
import { StyledSideBarHeader, StyledSideBarInfoBox, StyledSidebarInvitesButton, StyledSidebarTopicButton, createStyledIndicatorButton } from "../../../common/StyledComponents/SideBarComponents";
import addIcom from "../../../assets/images/chats/Add.svg";
import addBlackIcom from "../../../assets/images/chats/AddBlack.svg";
import { useDispatch, useSelector } from "react-redux";
import { requestArchiveGroup } from "../../../appStore/actions/ArkchatGroup/action";
import { saveSelectedAppGroupData, handleHomeTabValue } from "../../../appStore/actions/AppState/action";
import ProfileDetailModal from "../../../common/ProfileDetailModal";
import group_icon from "../../../assets/images/chats/Group_empty.svg";
import Archived from "../../../assets/images/chats/archived.svg";
import { colorSchemes, crossIconDiv, personIconStyle } from "../../../assets/commonFunction";
import InviteMemberDrawer from "../Drawer/InviteMemberDrawer";
import Archive from "../../../common/SvgImageView/Archive";
import {
  archiveGroupListSelector,
  currentHomeIndexTabSelector,
  currentUserDataSelector,
  groupListSelector,
  selectedGroupSelector,
} from "../../../customHooks/useAppSelectors";

// Made once: a styled component made while rendering is a new component each
// time (every button was thrown away and built again on each render).
const SmallActionButton = createStyledIndicatorButton("#FDFDFD");

const classes = {
  headingText: {
    color: "black",
    fontSize: "14px",
    fontWeight: "400",
    "@media (max-width: 63em)": {
      fontSize: "0.8rem",
    },
  },
  selectedItem: {
    backgroundColor: "#efefef",
  },
};

// Pinned aggregate entry shown above the real Groups & Contacts list. Selecting
// it (currently meaningful on Track Performance only) drives the analytics
// endpoints without a groupId, which the backend already treats as "every
// group I belong to, combined".
const ALL_GROUPS_ITEM = {
  id: "ALL_GROUPS",
  pnId: "ALL_GROUPS",
  name: "All Groups",
  isGroup: true,
  isAllGroups: true,
};

const SideBar = ({
  setisTopicOpen,
  handlecreategroup,
  handlegroupItemclick,
  handleFlipToArchiveGroupList,
  isCollapsed,
  setCollapsed,
}) => {
  const { t } = useTranslation();
  const [showArchiveIcons, setShowArchiveIcons] = useState(false);
  const [showUnArchiveIcons, setShowUnArchiveIcons] = useState(false);
  const [showArchiveList, setShowArchiveList] = useState(false);
  const appContent = t("AppContent", { returnObjects: true });
  const dispatch = useDispatch();
  // Only the values used here (useAppSelectors() would re-render this on any
  // of ~45 store values changing).
  const selectedGroup = useSelector(selectedGroupSelector);
  const groupList = useSelector(groupListSelector);
  const archiveGroupList = useSelector(archiveGroupListSelector);
  const currentHomeIndexTab = useSelector(currentHomeIndexTabSelector);
  const allWorkspaces = useSelector(allWorkspacesSelector);

  const [isInviteOpen, setInviteOpen] = useState(false);
  const [isProfileViewOpen, setProfileViewModal] = useState(false);
  const [profileViewData, setProfileViewData] = useState("");
  const [search, setSearch] = useState("");
  const [archiveSearch, setArchiveSearch] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Only the values used here (useAppSelectors() would re-render this on any
  // of ~45 store values changing).
  const currentUserData = useSelector(currentUserDataSelector);

  const handleOpenMenu = () => {
    setInviteOpen(true);
  };

  const handleCloseMenu = () => {
    setInviteOpen(false);
  };

  const activeWorkspaceId = currentUserData?.activeSubscriber?.id;
  const chatsShown = useMemo(
    () => chatsInView(groupList, allWorkspaces, activeWorkspaceId),
    [groupList, allWorkspaces, activeWorkspaceId]
  );
  const archivedChatsShown = useMemo(
    () => chatsInView(archiveGroupList, allWorkspaces, activeWorkspaceId),
    [archiveGroupList, allWorkspaces, activeWorkspaceId]
  );

  const filteredGroups = useMemo(() => {
    const baseList =
      currentHomeIndexTab === 0
        ? chatsShown
        : chatsShown?.filter((item) => item?.isGroup);

    if (!search) return baseList;

    return baseList?.filter((item) =>
      item?.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, chatsShown, currentHomeIndexTab]);

  const filteredArchiveGroups = useMemo(() => {
    const baseList =
      currentHomeIndexTab === 0
        ? archivedChatsShown
        : archivedChatsShown?.filter((item) => item?.isGroup);

    if (!archiveSearch) return baseList;

    return baseList?.filter((item) =>
      item?.name?.toLowerCase().includes(archiveSearch.toLowerCase())
    );
  }, [archiveSearch, archivedChatsShown, currentHomeIndexTab]);

  const visibleArchiveCount =
    currentHomeIndexTab === 0
      ? archivedChatsShown?.length || 0
      : archivedChatsShown?.filter((item) => item?.isGroup)?.length || 0;

  const handleOnSearchGroup = (event) => {
    if (showArchiveList) {
      setArchiveSearch(event.target.value);
      return;
    }
    setSearch(event.target.value);
  };

  const handleGroupProfileBadgeClick = (
    data,
    isGroup,
    isBuyerSellerGroup = false
  ) => {

    const viewData = {
      companyName:
        data?.accountDetails?.subscriberFirmName ||
        data?.activeSubscriber?.subscriberFirmName,
      userName: isGroup
        ? null
        : data?.name || `${data?.firstName} ${data?.lastName}`,
      industryName:
        data?.accountDetails?.subscriberAccountIndustry ||
        data?.activeSubscriber?.subscriberAccountIndustry,
      cityName: data?.accountDetails?.city || data?.city,
      industry:
        data?.accountDetails?.subscriberAccountIndustry ||
        data?.subscriberAccountIndustry,
      type:
        data?.accountDetails?.subscriberAccountType ||
        data?.subscriberAccountType,
      countryName: data?.accountDetails?.country || data?.country,
      profileBrief: data?.accountDetails?.profileBrief || data?.profileBrief,
      profileImgUrl: data?.accountDetails?.profileImgUrl || data?.profileImgUrl,
      coverImgUrl: data?.accountDetails?.coverImgUrl || data?.coverImgUrl,
      isGroup: isGroup,
      isBuyerSellerGroup: isBuyerSellerGroup,
      website: data?.accountDetails?.website || data?.website,
      linkedin: data?.accountDetails?.linkedin || data?.linkedin,
      joinedDate: data?.joinedDate
        ? new Date(
          Number(String(data?.joinedDate).slice(0, 13))
        ).toLocaleDateString()
        : data?.joinedDate,
      memberCount: data?.memberCount || data?.memberCount,
      createdDate: data?.createdDate
        ? new Date(
          Number(String(data?.createdDate).slice(0, 13))
        ).toLocaleDateString()
        : data?.createdDate,
      groupName: data?.name,
      // Lets Group Information follow live edits of this group.
      groupId: isGroup ? data?.id : undefined,
      groupOwner: data?.createdByUserDetails
        ? `${data.createdByUserDetails.firstName || ""} ${data.createdByUserDetails.lastName || ""
          }`.trim()
        : "-",
      description: data?.description,
      email: data?.createdByUserDetails?.email,
    };
    setProfileViewData(viewData);
    setProfileViewModal(true);
  };

  const handleArchiveClick = (item) => {
    dispatch(requestArchiveGroup(item?.pnId));
    setShowArchiveIcons(false);
    setShowUnArchiveIcons(false);
  };

  // The list's scrolling box (the rows are rendered as they come into view).
  const [scrollParent, setScrollParent] = useState(null);
  const onBadgeClick = useCallback(
    (item) => (item?.isGroup
      ? handleGroupProfileBadgeClick(item, item?.isGroup, item?.isBuyerSellerGroup)
      : handleGroupProfileBadgeClick(item, item?.isGroup)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const renderRow = (item, showAction, actionMode) => (
    <div style={{ paddingBottom: "0.4rem", width: "100%" }}>
      <ChatListRow
        item={item}
        isSelected={selectedGroup?.pnId === item?.pnId && selectedGroup?.name === item?.name}
        isSelf={!item?.isGroup && item?.id === currentUserData?.id}
        showAction={showAction}
        actionMode={actionMode}
        hideBadge={item?.pnId === selectedGroup?.pnId}
        selfLabel={appContent?.KEY5}
        timeFormat={currentUserData?.userTimeFormat}
        dateFormat={currentUserData?.userDateFormat}
        onBadgeClick={onBadgeClick}
        onItemClick={handlegroupItemclick}
        onActionClick={handleArchiveClick}
      />
    </div>
  );

  // Bypasses handlegroupItemclick on purpose: that handler fetches messages,
  // topics, and group member details for the clicked group, none of which
  // apply to the synthetic "All Groups" entry. This just sets selectedGroup
  // and jumps to Track Performance, the only tab that currently knows how to
  // render a combined view.
  const handleAllGroupsClick = () => {
    dispatch(saveSelectedAppGroupData(ALL_GROUPS_ITEM));
    dispatch(handleHomeTabValue(2));
  };

  const handleShowArchiveList = (action) => {
    handleFlipToArchiveGroupList();
    setisTopicOpen(false);
    setShowArchiveList(action);
    setSearch("");
    setArchiveSearch("");
    setIsSearchOpen(false);
  };

  if (isCollapsed) {
    return (
      <Stack
        bgcolor={"#FDFDFD"}
        height={"100%"}
        alignItems={"center"}
        sx={{ cursor: "pointer" }}
        onClick={() => setCollapsed(false)}
      >
        <Box
          width={"100%"}
          minHeight={"38px"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          bgcolor={"#efefef"}
          marginBottom={"0.2rem"}
        >
          <KeyboardDoubleArrowRightIcon
            sx={{ fontSize: "1.3rem", color: "#000" }}
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            ...classes.headingText,
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            mt: "0.5rem",
            whiteSpace: "nowrap",
          }}
        >
          Groups & Contacts
        </Typography>
        <Box flex={1} minHeight={0} />
        <Box
          width={"100%"}
          display={"flex"}
          justifyContent={"center"}
          padding={"0.5rem 0"}
          bgcolor={"#FDFDFD"}
        >
          <SmallActionButton
            size="small"
            variant="contained"
            sx={{
              padding: currentUserData?.profileImgUrl ? "0px" : "6px",
              overflow: "hidden",
            }}
          >
            {currentUserData?.profileImgUrl ? (
              <img
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                src={currentUserData?.profileImgUrl}
                alt=""
              />
            ) : (
              <img style={personIconStyle} src={PersonIcon} alt="individual" />
            )}
          </SmallActionButton>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack bgcolor={"#FDFDFD"} height={"100%"}>
      <ProfileDetailModal
        setProfileViewModal={setProfileViewModal}
        isProfileViewOpen={isProfileViewOpen}
        profileViewData={profileViewData}
        setProfileViewData={setProfileViewData}
      />
      <StyledSideBarHeader
        sx={{
          bgcolor: "#efefef",
          alignItems: "center",
          marginBottom: 0,
        }}
        position={"relative"}
      >
        {!showArchiveList && (
          <>
            <Box
              display="flex"
              alignItems="center"
              gap={"0.3rem"}
              marginLeft={"0.1rem"}
              flex={1}
              minWidth={0}
            >
              <KeyboardDoubleArrowLeftIcon
                onClick={() => setCollapsed(true)}
                sx={{ fontSize: "1.3rem", color: "#000", cursor: "pointer", flexShrink: 0 }}
              />
              {isSearchOpen ? (
                <SearchBox sx={{ flex: 1, mr: "0.3rem" }}>
                  <CustomInput
                    autoFocus
                    value={search}
                    placeholder={appContent?.KEY303}
                    onChange={handleOnSearchGroup}
                  />
                  <ChatSearchButton
                    size="small"
                    variant="contained"
                    onClick={() => { setIsSearchOpen(false); setSearch(""); }}
                  >
                    <CloseIcon sx={{ fontSize: "1rem", color: "#000" }} />
                  </ChatSearchButton>
                </SearchBox>
              ) : (
                <Typography sx={classes.headingText} variant="h6">
                  Groups & Contacts
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }} gap={"0.3rem"} flexShrink={0}>
              {!isSearchOpen && (
                <Button
                  onClick={() => setIsSearchOpen(true)}
                  size="small"
                  variant="contained"
                  sx={{
                    padding: "8px",
                    border: "none",
                    backgroundColor: "transparent",
                    width: "1.87rem",
                    height: "1.87rem",
                    minWidth: "unset",
                    minHeight: "unset",
                    borderRadius: "0.15rem",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                  }}
                >
                  <SmallIcon src={searchIcon} />
                </Button>
              )}
              <StyledSidebarTopicButton
                sx={{ display: "flex", alignItems: "center", gap: "0.2rem" }}
                onClick={handlecreategroup}
                size="small"
              >
                <img src={addBlackIcom} alt="group" />
                {appContent?.KEY195}
              </StyledSidebarTopicButton>
            </Box>
          </>
        )}
        {showArchiveList && (
          <>
            <Box display="flex" alignItems="center" gap={"0.5rem"} flex={1} minWidth={0}>
              <CloseIcon
                sx={{ ...crossIconDiv, flexShrink: 0 }}
                onClick={() => handleShowArchiveList(false)}
              />
              {isSearchOpen ? (
                <SearchBox sx={{ flex: 1, mr: "0.3rem" }}>
                  <CustomInput
                    autoFocus
                    value={archiveSearch}
                    placeholder={appContent?.KEY303}
                    onChange={handleOnSearchGroup}
                  />
                  <ChatSearchButton
                    size="small"
                    variant="contained"
                    onClick={() => { setIsSearchOpen(false); setArchiveSearch(""); }}
                  >
                    <CloseIcon sx={{ fontSize: "1rem", color: "#000" }} />
                  </ChatSearchButton>
                </SearchBox>
              ) : (
                <Box>
                  <Typography sx={classes.headingText} variant="h6">
                    Archived
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }} gap={"0.3rem"} flexShrink={0}>
              {!isSearchOpen && (
                <Button
                  onClick={() => setIsSearchOpen(true)}
                  size="small"
                  variant="contained"
                  sx={{
                    padding: "8px",
                    border: "none",
                    backgroundColor: "transparent",
                    width: "1.87rem",
                    height: "1.87rem",
                    minWidth: "unset",
                    minHeight: "unset",
                    borderRadius: "0.15rem",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                  }}
                >
                  <SmallIcon src={searchIcon} />
                </Button>
              )}
              <StyledSidebarTopicButton
                onClick={() => setShowUnArchiveIcons(true)}
                sx={{ display: "flex", alignItems: "center", gap: "0.2rem" }}
                size="small"
              >
                Unarchive
              </StyledSidebarTopicButton>
            </Box>
          </>
        )}
      </StyledSideBarHeader>

      <Box minHeight={"5px"} bgcolor={"#FDFDFD"} width={"100%"} />

      <StyledSideBarInfoBox sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {!showArchiveList && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              cursor: "pointer",
              borderRadius: "0.1rem",
              marginBottom: "0.4rem",
            }}
          >
            {/* <Box
              width="1.99rem"
              height="1.89rem"
              borderRadius="2px"
              display="flex"
              justifyContent="center"
              backgroundColor="#E3E1E1"
              flexShrink={0}
              sx={{
                padding: "6px",
                overflow: "hidden",
              }}
            >
              <img src={Archived} alt="archived" style={{ width: "20px" }} />
            </Box> */}
            <Box onClick={() => handleShowArchiveList(true)} flex={1} minWidth={0} ml="1.2rem">
              <Typography fontSize="13px" fontWeight="400" color="black">
                Archived  {visibleArchiveCount}
              </Typography>
            </Box>
            <Button
              onClick={() => setShowArchiveIcons(!showArchiveIcons)}
              size="small"
              variant="contained"
              sx={{
                padding: "0px",
                border: `0.5px solid ${colorSchemes.black}`,
                backgroundColor: showArchiveIcons
                  ? `${colorSchemes.chipIconBlueColor}`
                  : `${colorSchemes.white}`,
                width: "1.87rem",
                height: "1.87rem",
                minWidth: "unset",
                minHeight: "unset",
                borderRadius: "0.15rem",
                boxShadow: "none",
                flexShrink: 0,
                marginRight: "0.1rem",
                "&:hover": {
                  backgroundColor: showArchiveIcons
                    ? `${colorSchemes.chipIconBlueColor}`
                    : `${colorSchemes.white}`,
                  boxShadow: "none",
                },
              }}
            >
              <Archive color={showArchiveIcons ? "#FFFF" : "#000000"} />
            </Button>
          </div>
        )}
        <Box
          ref={setScrollParent}
          bgcolor={"#FDFDFD"}
          display="flex"
          flexDirection="column"
          alignItems="center"
          mt={"0.4rem"}
          rowGap={"0.4rem"}
          overflow={"scroll"}
          flex={1}
          minHeight={0}
          sx={{
            "&::-webkit-scrollbar": {
              width: "0px",
              height: "0px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#FDFDFD",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "grey",
            },
          }}
        >
          {!showArchiveList && (
            <>
              {/* Pinned "All Groups" entry only drives the combined analytics
                  view on Track Performance, so it's hidden on Chats and Track
                  Work where selecting it has no meaningful effect. */}
              {currentHomeIndexTab === 2 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    backgroundColor:
                      selectedGroup?.pnId === ALL_GROUPS_ITEM.pnId
                        ? classes.selectedItem.backgroundColor
                        : "transparent",
                    borderRadius: "0.1rem",
                  }}
                >
                  <Box
                    width={"100%"}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.1rem",
                      cursor: "pointer",
                    }}
                    onClick={handleAllGroupsClick}
                  >
                    <Box width={"15%"}>
                      <SmallActionButton
                        size="small"
                        variant="contained"
                        sx={{
                          padding: "6px",
                          overflow: "hidden",
                          backgroundColor:
                            selectedGroup?.pnId === ALL_GROUPS_ITEM.pnId
                              ? classes.selectedItem.backgroundColor
                              : "#FDFDFD",
                          "&:hover": {
                            backgroundColor:
                              selectedGroup?.pnId === ALL_GROUPS_ITEM.pnId
                                ? classes.selectedItem.backgroundColor
                                : "#FDFDFD",
                          },
                        }}
                      >
                        <img
                          style={{ width: "100%", height: "100%" }}
                          src={group_icon}
                          alt="all groups"
                        />
                      </SmallActionButton>
                    </Box>
                    <Box width={"84%"} padding={"0.2rem 0.4rem"} borderRadius="0.1rem">
                      <Typography fontSize="13px" fontWeight="600" color="black" variant="h6">
                        All Groups
                      </Typography>
                    </Box>
                  </Box>
                </div>
              )}

              {/* Only the rows in view are rendered: someone in a big account has
                  a contact per member (2,000 rows, each redrawn on every update). */}
              {scrollParent && (
                <Virtuoso
                  style={{ width: "100%" }}
                  customScrollParent={scrollParent}
                  data={filteredGroups || []}
                  increaseViewportBy={400}
                  computeItemKey={(i, item) => item?.id + "groupList" + i + "unique"}
                  itemContent={(i, item) => renderRow(item, showArchiveIcons, "archive")}
                />
              )}
            </>
          )}

          {/* Archived List */}
          {showArchiveList && (
            <>
              {scrollParent && (
                <Virtuoso
                  style={{ width: "100%" }}
                  customScrollParent={scrollParent}
                  data={filteredArchiveGroups || []}
                  increaseViewportBy={400}
                  computeItemKey={(i, item) => item?.id + "archiveGroupList" + i + "unique"}
                  itemContent={(i, item) => renderRow(item, showUnArchiveIcons, "unarchive")}
                />
              )}
            </>
          )}
        </Box>
      </StyledSideBarInfoBox>
      <Box
        display={"flex"}
        padding={"0.5rem 0.3rem"}
        bgcolor={"#efefef"}
        alignItems={"center"}
        width={"100%"}
      >
        <SmallActionButton
          size="small"
          variant="contained"
          sx={{
            mr: "0.5rem",
            padding: currentUserData?.profileImgUrl ? "0px" : "6px",
            overflow: "hidden",
            flexShrink: 0,
          }}

          onClick={() =>
            handleGroupProfileBadgeClick(currentUserData)
          }
        >
          {currentUserData?.profileImgUrl ?
            <img
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              src={currentUserData?.profileImgUrl}
              alt=""
            /> :
            <img style={personIconStyle} src={PersonIcon} alt="individual" />}

        </SmallActionButton>
        <Typography
          textAlign={"start"}
          fontSize="14px"
          color={"#000000"}
          variant="h6"
          title={`${currentUserData?.firstName} ${currentUserData?.lastName}`}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
            mr: "0.5rem",
          }}
        >
          {currentUserData?.firstName}{" "}
          {currentUserData?.lastName}
        </Typography>

        <StyledSidebarInvitesButton
          sx={{ fontSize: "0.80rem", flexShrink: 0 }}
          onClick={handleOpenMenu}
        >
          <img src={addIcom} alt="group" />
          <Typography ml={"0.2rem"} fontSize={"0.80rem"}>
            {appContent?.KEY9}
          </Typography>
        </StyledSidebarInvitesButton>

        <InviteMemberDrawer
          isaddmemberOpen={isInviteOpen}
          closePopUp={handleCloseMenu}
          handleGroupProfileBadgeClick={handleGroupProfileBadgeClick}
          classes={classes}
        />
      </Box>
    </Stack >
  );
};

// Memoized: re-renders only when its props or store values change, not on
// every render of the page around it (e.g. each keystroke in the message box).
export default React.memo(SideBar);
